import { FlowDiagram, TechTag, FadeIn } from "./common";
import { CategoryBackground } from "./SciBackground";
import CategoryHeader from "./CategoryHeader";
import SignalViewer from "./SignalViewer";

const HARVESTER_LAYERS = [
  {
    title: "Sources",
    items: [
      { label: "Source A — File Store", detail: "CSV/JSON exports on shared file stores, collected on schedule rather than by hand." },
      { label: "Source B — Application Endpoint", detail: "REST API retrieval with authentication handled outside the codebase and responses validated against schema." },
      { label: "Source C — Operational System", detail: "Manufacturing metadata harvested in incremental windows keyed on source timestamps." },
      { label: "Source D — Product Quality Export", detail: "Large result files processed in batches with checkpoint/resume so long jobs survive interruption." },
    ],
  },
  {
    title: "Processing",
    items: [
      { label: "Automated Acquisition", detail: "Scheduled Python harvesters replace repetitive manual retrieval." },
      { label: "Validation", detail: "Schema and range checks quarantine malformed input to an exception log with source and reason." },
      { label: "Normalization & Mapping", detail: "Heterogeneous schemas conform to a canonical model; identifiers map through explicit tables." },
      { label: "Feature Extraction", detail: "Analysis-ready fields are derived deterministically and reproducibly." },
    ],
  },
  {
    title: "Outputs",
    items: [
      { label: "Master Dataset", detail: "Deduplicated, traceable, analysis-ready — every record retains source identity and retrieval time." },
      { label: "Analytics / RCA", detail: "Investigations start from prepared evidence instead of a collection task." },
    ],
  },
];

const RECON_LAYERS = [
  {
    title: "Reconciliation Flow",
    items: [
      { label: "Manufacturing Record", detail: "The record whose lot attributes must be verified." },
      { label: "Lot Identifier", detail: "Normalized lot key used to join across source systems." },
      { label: "Source Evidence", detail: "Every candidate source record retained — nothing is silently discarded." },
    ],
  },
  {
    title: "Engine",
    items: [
      { label: "Mapping Engine", detail: "Deterministic mapping rules produce candidate matches with rationale." },
      { label: "Conflict Detection", detail: "Conflicting or unresolved mappings surface explicitly for review — never auto-guessed." },
      { label: "Evidence Table", detail: "Each reconciled value links to the source records that justify it." },
    ],
  },
  {
    title: "Output",
    items: [
      { label: "Reconciled Master Dataset", detail: "Trusted genealogy for analytics and investigation, with a managed unresolved queue." },
    ],
  },
];

export default function Automation() {
  return (
    <section id="automation" className="relative overflow-hidden scroll-mt-16" data-testid="automation-section">
      <CategoryBackground variant="pipeline" accent="cyan" accent2="eblue" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20">
      <CategoryHeader
        id="automation"
        category="automation"
        eyebrow="Process Automation"
        title="Automation that investigations can trust"
        description="Python-based data acquisition, API integration, validation logic and automated workflow design — schema validation, reconciliation and source traceability that convert manual data work into scheduled, auditable digital pipelines with checkpoint/resume and failure recovery."
      />

      <FadeIn>
        <div className="mt-10">
          <h3 className="font-sans tracking-tight text-xl text-paper mb-1">Multi-Source Manufacturing Data Harvester</h3>
          <p className="text-sm text-steel mb-5 max-w-3xl">
            Manufacturing and Product Quality information distributed across file stores, endpoints and operational systems is collected, validated, normalized and reconciled automatically.
          </p>
          <FlowDiagram layers={HARVESTER_LAYERS} testId="harvester-flow" />
          <div className="flex flex-wrap gap-1.5 mt-4">
            {["Python", "pandas", "REST APIs", "CSV", "JSON", "Batch Processing", "Large-File Processing", "Schema Handling", "Data Mapping", "Deduplication", "Checkpoint/Resume", "Exception Logging", "Validation", "Source Traceability"].map((t) => (
              <TechTag key={t}>{t}</TechTag>
            ))}
          </div>
        </div>
      </FadeIn>

      <FadeIn>
        <div className="mt-16">
          <h3 className="font-sans tracking-tight text-xl text-paper mb-1">Automated Analytical Run-File Signal Harvester</h3>
          <p className="text-sm text-steel mb-5 max-w-3xl">
            Batch extraction of peaks, thresholds, markers, noise baselines and candidate artifacts from large run-file populations — with audit-friendly output.
          </p>
          <SignalViewer />
        </div>
      </FadeIn>

      <FadeIn>
        <div className="mt-16">
          <h3 className="font-sans tracking-tight text-xl text-paper mb-1">Automated Lot &amp; Source-Record Reconciliation Engine</h3>
          <p className="text-sm text-steel mb-5 max-w-3xl">
            Lot attributes reconciled against multiple source records with evidence traceability and explicit conflict detection.
          </p>
          <FlowDiagram layers={RECON_LAYERS} testId="reconciliation-flow" />
          <div className="flex flex-wrap gap-1.5 mt-4">
            {["Reconciliation", "Mapping", "Traceability", "Exception Handling", "Evidence Retention", "Conflict Detection", "Repeatable Processing"].map((t) => (
              <TechTag key={t}>{t}</TechTag>
            ))}
          </div>
        </div>
      </FadeIn>
      </div>
    </section>
  );
}
